import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  activeFilterCount,
  type CatalogFilters,
  type CatalogSort,
  matchesFilters,
} from '@/entities/catalog';
import { readRecentDatabaseIds, useMyDatabases } from '@/entities/database';
import type { Workspace } from '@/entities/workspace';
import { DatabaseList } from '@/features/database-catalog';

import styles from './HomeDashboard.module.css';
import { SectionHead } from './SectionHead';
import { catalogLink, databaseFilterable, HOME_OFFERED } from './home-filters';

const SHOWN = 12;

/**
 * Databases the viewer can reach in all their workspaces (hidden confidential ones are never
 * listed), narrowed by the SAME search and filters as the boards above. The block stays out of
 * the way while there are none, or while the type filter asks for boards only.
 */
export function HomeDatabases({
  workspaces,
  query,
  filters,
  sort,
  userId,
  locale,
}: {
  workspaces: readonly Workspace[];
  query: string;
  filters: CatalogFilters;
  sort: CatalogSort;
  userId: string | null;
  locale: string;
}) {
  const { t } = useTranslation();
  const databases = useMyDatabases();
  const names = useMemo(
    () =>
      new Map(
        workspaces.map((w) => [
          w.id,
          w.workspace_type === 'personal' ? t('home.personalWorkspace') : w.name,
        ]),
      ),
    [workspaces, t],
  );
  const spaces = useMemo(
    () => new Map(workspaces.map((w) => [w.id, w.workspace_type] as const)),
    [workspaces],
  );
  const matches = useMemo(() => {
    const order = userId ? readRecentDatabaseIds(userId) : [];
    const opened = new Set(order);
    const list = (databases.data ?? []).filter((database) =>
      matchesFilters(
        databaseFilterable(database, opened, spaces, names),
        filters,
        userId,
        query,
        HOME_OFFERED,
      ),
    );
    if (sort === 'name') {
      return list.toSorted(
        (a, b) => a.name.localeCompare(b.name, locale) || a.id.localeCompare(b.id),
      );
    }
    if (sort === 'opened') {
      const rank = (id: string) => order.indexOf(id) + 1 || Number.MAX_SAFE_INTEGER;
      return list.toSorted((a, b) => rank(a.id) - rank(b.id));
    }
    return list;
  }, [databases.data, spaces, names, filters, userId, query, sort, locale]);
  if (filters.type === 'board') return null;
  // "Databases" asked for explicitly: the section stays to say there are none; otherwise a person
  // without any database never sees an empty block.
  if (!databases.isSuccess || (databases.data.length === 0 && filters.type !== 'database')) {
    return null;
  }
  const narrowed = query.trim() !== '' || activeFilterCount(filters, HOME_OFFERED) > 0;
  return (
    <section className={styles.databases} aria-labelledby="home-databases-title">
      <SectionHead
        id="home-databases-title"
        title={t('databases.title')}
        count={matches.length}
        to={catalogLink('/app/browse/databases', query)}
        linkLabel={t('home.viewAllDatabases')}
        linkTestId="all-databases-link"
      />
      <DatabaseList
        databases={matches.slice(0, SHOWN)}
        workspaceNames={names}
        emptyText={narrowed ? t('databases.noMatches') : t('databases.empty')}
      />
      {matches.length > SHOWN && (
        <p className={styles.moreNote}>
          {t('databases.moreInWorkspaces', { count: matches.length - SHOWN })}
        </p>
      )}
    </section>
  );
}
